import { useCallback, useEffect, useState } from "react"
import { useLocation, Link } from "react-router-dom"
import { X, Sparkles, Star, Zap, Gift, Info, type LucideIcon } from "lucide-react"
import { useBranding, type StripColor, type StripIcon, type PortalStripConfig } from "../lib/useBranding"
import { useAuth } from "./AuthProvider"
import { ROUTES } from "../lib/routes"
import { isDemoSite } from "../lib/isDemoSite"
import { GlitchWord } from "./GlitchWord"

const ICON_MAP: Record<StripIcon, LucideIcon> = {
  sparkles: Sparkles,
  star: Star,
  zap: Zap,
  gift: Gift,
  info: Info,
}

const COLOR_CLASSES: Record<StripColor, string> = {
  teal:   "text-teal-400",
  luster: "text-luster-400",
  red:    "text-red-400",
  violet: "text-violet-400",
  amber:  "text-amber-400",
}

const COLOR_BASE_MAP: Record<StripColor, "teal" | "violet" | "mixed"> = {
  teal:   "teal",
  luster: "violet",
  red:    "mixed",
  violet: "violet",
  amber:  "mixed",
}

const BTN_BASE: Record<StripColor, string> = {
  teal:   "bg-teal-500/15 border-teal-500/40 text-teal-200 hover:bg-teal-500/25 hover:border-teal-400/50",
  luster: "bg-luster-500/15 border-luster-500/40 text-luster-200 hover:bg-teal-500/20 hover:border-teal-400/30",
  red:    "bg-red-500/15 border-red-500/40 text-red-200 hover:bg-teal-500/20 hover:border-teal-400/30",
  violet: "bg-violet-500/15 border-violet-500/40 text-violet-200 hover:bg-teal-500/20 hover:border-teal-400/30",
  amber:  "bg-amber-500/15 border-amber-500/40 text-amber-200 hover:bg-teal-500/20 hover:border-teal-400/30",
}

const BTN_HOVER: Record<StripColor, string> = {
  teal:   "hover:text-teal-100",
  luster: "hover:text-teal-100",
  red:    "hover:text-teal-100",
  violet: "hover:text-teal-100",
  amber:  "hover:text-teal-100",
}

function buttonClasses(base: StripColor, hover: StripColor | null): string {
  const hoverColor = hover ?? base
  return `${BTN_BASE[base]} ${BTN_HOVER[hoverColor]}`
}

/** djb2 hash of strip text + buttonText + buttonUrl — changes when creator pushes new message. */
function contentHash(config: PortalStripConfig): string {
  const raw = [config.text, config.buttonText, config.buttonUrl].join("\x00")
  let h = 5381
  for (let i = 0; i < raw.length; i++) {
    h = ((h << 5) + h) ^ raw.charCodeAt(i)
    h |= 0
  }
  return (h >>> 0).toString(36)
}

function dismissKey(hash: string): string {
  return `portalStrip:dismissed:${hash}`
}

/** Demo sites use sessionStorage so refresh always restores the strip. */
function getStorage(): Storage {
  return isDemoSite() ? sessionStorage : localStorage
}

/**
 * Portal Strip — fixed top-bar glass card. Lives in LayoutV2 alongside the hamburger.
 *
 * Visibility rules:
 * - creator / admin: all pages
 * - all other roles: dashboard (/) only
 *
 * Dismiss: clicking × stores the content hash in localStorage. Reappears automatically
 * when the creator pushes a new message (text, buttonText, or buttonUrl changes).
 */
export function PortalStrip() {
  const { branding } = useBranding()
  const { isCreator, role } = useAuth()
  const location = useLocation()
  const config = branding?.portalStrip

  const hash = config ? contentHash(config) : null
  const [dismissed, setDismissed] = useState(() => {
    if (!hash) return false
    return getStorage().getItem(dismissKey(hash)) === "1"
  })

  // Re-evaluate dismiss state when the hash changes (new message pushed).
  useEffect(() => {
    if (!hash) return
    setDismissed(getStorage().getItem(dismissKey(hash)) === "1")
  }, [hash])

  const dismiss = useCallback(() => {
    if (!hash) return
    getStorage().setItem(dismissKey(hash), "1")
    setDismissed(true)
  }, [hash])

  if (!config) return null
  if (config.activeUntil && new Date(config.activeUntil) < new Date()) return null
  if (dismissed) return null

  // Non-creator/admin roles only see the strip on the dashboard.
  const isAdminOrCreator = isCreator || role === "admin"
  if (!isAdminOrCreator && location.pathname !== ROUTES.dashboard) return null

  const Icon = ICON_MAP[config.icon]
  const isInternalLink = config.buttonUrl.startsWith("/")

  const glitchColorBase = COLOR_BASE_MAP[config.iconColor]
  const firstWord = config.text.split(" ")[0]

  const buttonEl = (
    <span className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full transition-colors duration-200 border font-mono text-[10px] sm:text-[11px] font-bold whitespace-nowrap ${buttonClasses(config.buttonColor, config.buttonHoverColor)}`}>
      <GlitchWord word={config.buttonText} intensity={1} colorBase={glitchColorBase} />
    </span>
  )

  return (
    <div className="pointer-events-auto flex-1 min-w-0 flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-2.5 rounded-2xl border border-white/[0.08] bg-black/40 backdrop-blur-2xl shadow-[0_0_24px_-4px_rgba(0,0,0,0.4)]">
      <span className="hidden sm:inline font-mono text-[9px] text-white/10 tabular-nums shrink-0">0x</span>
      <Icon className={`w-3 h-3 sm:w-3.5 sm:h-3.5 shrink-0 ${COLOR_CLASSES[config.iconColor]}`} />
      <span className="font-mono text-[10px] sm:text-[11px] tracking-wide text-white/60 flex-1 min-w-0 truncate">
        {config.textMobile ? (
          <>
            <span className="sm:hidden">
              <GlitchWord word={config.textMobile} intensity={1} colorBase={glitchColorBase} />
            </span>
            <span className="hidden sm:inline">
              <GlitchWord word={firstWord} intensity={1} colorBase={glitchColorBase} />
              {config.text.slice(firstWord.length)}
            </span>
          </>
        ) : (
          <>
            <GlitchWord word={firstWord} intensity={1} colorBase={glitchColorBase} />
            {config.text.slice(firstWord.length)}
          </>
        )}
      </span>
      {isInternalLink ? (
        <Link to={config.buttonUrl} className="shrink-0">
          {buttonEl}
        </Link>
      ) : (
        <a href={config.buttonUrl} target="_blank" rel="noopener noreferrer" className="shrink-0">
          {buttonEl}
        </a>
      )}
      <button
        type="button"
        onClick={dismiss}
        className="shrink-0 w-5 h-5 flex items-center justify-center rounded-full text-white/30 hover:text-white/60 hover:bg-white/[0.06] transition-colors duration-150 focus:outline-none"
        aria-label="Dismiss strip"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  )
}
